import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, ThemeProvider, createTheme, CircularProgress } from '@mui/material';
import dropboxService from '../utils/dropboxService';

const ConfigContainer = styled.div`
  padding: 20px;
`;

const StatusCard = styled.div`
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  ${props => props.connected ? `
    background: rgba(var(--al-primary-rgb), 0.1);
    border: 1px solid rgba(var(--al-primary-rgb), 0.3);
  ` : `
    background: rgba(255, 165, 0, 0.1);
    border: 1px solid rgba(255, 165, 0, 0.3);
  `}
`;

const StatusIcon = styled.span`
  font-size: 1.5rem;
  margin-right: 8px;
`;

const ConnectButton = styled(Button)`
  background: #0061FF !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 12px 24px !important;
  font-size: 1rem !important;
  
  &:hover {
    background: #0052D9 !important;
  }
  
  &:disabled {
    background: #666 !important;
  }
`;

const DisconnectButton = styled(Button)`
  background: #dc3545 !important;
  color: white !important;
  
  &:hover {
    background: #c82333 !important;
  }
`;

const TestButton = styled(Button)`
  margin-top: 12px !important;
`;

// Create a theme for the modal
const modalTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#98FB98',
    },
    background: {
      default: '#2C2C2C',
      paper: '#2C2C2C',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: 'Montserrat, sans-serif',
    button: {
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#2C2C2C',
          color: '#ffffff',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontFamily: 'Montserrat, sans-serif',
        },
      },
    },
  },
});

const DropboxConfig = ({ isOpen, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({ connected: false });
  const [accountInfo, setAccountInfo] = useState(null);

  // Check connection status when modal opens
  useEffect(() => {
    if (isOpen) {
      checkConnectionStatus();
    }
  }, [isOpen]);

  const checkConnectionStatus = async () => {
    setLoading(true);
    try {
      const status = await dropboxService.getConnectionStatus();
      setConnectionStatus(status);
      
      // If connected, try to get account info
      if (status.connected) {
        try {
          dropboxService.initialize();
          const info = await dropboxService.testConnection();
          setAccountInfo(info);
        } catch (error) {
          console.warn('Could not get account info:', error);
        }
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
      setConnectionStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      // Get the current URL for the redirect
      const redirectUri = `${window.location.origin}/auth/dropbox/callback`;
      
      // Get the authorization URL
      const authUrl = await dropboxService.startOAuthFlow(redirectUri);
      
      // Redirect to Dropbox for authorization
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error starting OAuth flow:', error);
      alert(`Failed to start Dropbox connection: ${error.message}`);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Dropbox? You will need to reconnect to upload invoices.')) {
      return;
    }
    
    setDisconnecting(true);
    try {
      await dropboxService.disconnect();
      setConnectionStatus({ connected: false });
      setAccountInfo(null);
      onSave(null);
      alert('Dropbox disconnected successfully.');
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert(`Failed to disconnect: ${error.message}`);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTestUpload = async () => {
    setTesting(true);
    try {
      dropboxService.initialize();
      
      // Create a test file
      const testContent = `Test upload from TrollShipper at ${new Date().toISOString()}`;
      const testBlob = new Blob([testContent], { type: 'text/plain' });
      const filename = `test-upload-${Date.now()}.txt`;
      
      console.log('Testing upload to Dropbox:', filename);
      const result = await dropboxService.uploadFile(testBlob, filename, '/87 INVOICES');
      
      alert(`✅ Test upload successful!\n\nFile: ${filename}\nPath: ${result.path}\nURL: ${result.url || 'No shared link'}`);
    } catch (error) {
      console.error('Test upload failed:', error);
      alert(`❌ Test upload failed!\n\nError: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const handleRefreshToken = async () => {
    setTesting(true);
    try {
      await dropboxService.refreshToken();
      alert('✅ Token refreshed successfully!');
      await checkConnectionStatus();
    } catch (error) {
      console.error('Token refresh failed:', error);
      alert(`❌ Token refresh failed: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <ThemeProvider theme={modalTheme}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: 'var(--al-surface)',
            color: '#ffffff',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          📦 Dropbox Configuration
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <ConfigContainer>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <CircularProgress sx={{ color: 'var(--al-primary)' }} />
                <Typography sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                  Checking connection status...
                </Typography>
              </div>
            ) : (
              <>
                {/* Connection Status Card */}
                <StatusCard connected={connectionStatus.connected}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <StatusIcon>
                      {connectionStatus.connected ? '✅' : '⚠️'}
                    </StatusIcon>
                    <div>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {connectionStatus.connected ? 'Connected' : 'Not Connected'}
                      </Typography>
                      {connectionStatus.connected && accountInfo && (
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {accountInfo.name?.display_name || accountInfo.email || 'Dropbox Account'}
                        </Typography>
                      )}
                      {connectionStatus.connected && connectionStatus.connectedAt && (
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          Connected: {new Date(connectionStatus.connectedAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </div>
                  </div>
                </StatusCard>

                {/* Description */}
                <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.7)' }}>
                  {connectionStatus.connected 
                    ? 'Your Dropbox account is connected. Invoice PDFs will automatically upload to the /87 INVOICES folder.'
                    : 'Connect your Dropbox account to automatically upload invoice PDFs. This uses OAuth for secure, permanent access - no more expired tokens!'
                  }
                </Typography>

                {/* Connect/Disconnect Button */}
                {connectionStatus.connected ? (
                  <>
                    <TestButton
                      variant="outlined"
                      fullWidth
                      onClick={handleTestUpload}
                      disabled={testing}
                      sx={{ mb: 1 }}
                    >
                      {testing ? '🔄 Testing...' : '🧪 Test Upload to /87 INVOICES'}
                    </TestButton>
                    
                    <TestButton
                      variant="outlined"
                      fullWidth
                      onClick={handleRefreshToken}
                      disabled={testing}
                      sx={{ mb: 2 }}
                    >
                      {testing ? '🔄 Refreshing...' : '🔄 Refresh Access Token'}
                    </TestButton>
                    
                    <DisconnectButton
                      variant="contained"
                      fullWidth
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                    >
                      {disconnecting ? 'Disconnecting...' : '🔌 Disconnect Dropbox'}
                    </DisconnectButton>
                  </>
                ) : (
                  <ConnectButton
                    variant="contained"
                    fullWidth
                    onClick={handleConnect}
                    disabled={connecting}
                    startIcon={connecting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                  >
                    {connecting ? 'Connecting...' : '🔗 Connect Dropbox Account'}
                  </ConnectButton>
                )}

                {/* OAuth Info */}
                <Typography variant="caption" sx={{ display: 'block', mt: 3, color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center' }}>
                  {connectionStatus.connected 
                    ? 'Tokens automatically refresh - no manual intervention needed!'
                    : 'You will be redirected to Dropbox to authorize this app.'
                  }
                </Typography>
              </>
            )}
          </ConfigContainer>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
          <Button onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default DropboxConfig;
